import { CssBaseline } from "@mui/material";
import { alpha, createTheme, ThemeProvider } from "@mui/material/styles";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { colours } from "../../consts";
import { THEME_MODES } from "../../helpers/constant";
import { useThemeStore } from "../../zustand/store";
import CustomSvgIcons from "../svg-icons";

// Create Theme Context
const ThemeContext = createContext({
    colours,
    toggleTheme: () => { },
    themeMode: "",
    setThemeMode: () => { },
    FONT: {},
});

// Custom Hook for Theme Access
export const useThemeMode = () => useContext(ThemeContext);

// Theme Provider Component
export const CustomThemeProvider = ({ children }) => {
    const { currentTheme, setCurrentTheme, fontSizeLevel } = useThemeStore();
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemTheme = isDarkMode ? THEME_MODES.DARK : THEME_MODES.LIGHT;
    if (!currentTheme) {
        setCurrentTheme(systemTheme);
    }

    const [themeMode, setThemeMode] = useState(currentTheme || systemTheme);

    // Define Font Sizes
    const FONT = useMemo(() => {
        // Calculate base font size (e.g., 16px as normal)
        const baseFontSize = 16 + fontSizeLevel;

        return {
            SIZE_10: baseFontSize - 6,
            SIZE_12: baseFontSize - 4,
            SIZE_13: baseFontSize - 3,
            SIZE_14: baseFontSize - 2,
            SIZE_16: baseFontSize,
            SIZE_20: baseFontSize + 4,
            SIZE_24: baseFontSize + 8,
            SIZE_28: baseFontSize + 12,
            SIZE_32: baseFontSize + 16,
            SIZE_40: baseFontSize + 24,
        }
    }, [fontSizeLevel]);


    // Toggle Theme Function
    const toggleTheme = useCallback((newThemeMode) => {
        setCurrentTheme(newThemeMode);
        setThemeMode(newThemeMode);
    }, []);

    // Dark Mode Common Colors Palette
    const darkModePalettes = {
        blue10: "#232A33",
        blue20: "#2D415E",
        purple5: "#2B212E",
        purple20: "#45284F",
        success5: "#1E2B1B",
        success20: "#1F4615",
        orange10: "#32271E",
        orange20: "#5A381F",
        gray90: "#A0ABC0",
        error: "#FF8383",
        dividergray: colours.grayDisabled,
        grayDisabled: colours.gray60,
        success100: "#22ad00",
        green100: "#22ad00",
        white: colours.black70,
        basegray: colours.black,
        grayDark: colours.gray40,
        grayDark500: colours.black70,
        dividergraybottomborder: `1px solid ${colours.gray60}`,
        shadowMd: {
            boxShadow: `-2px 2px 3px ${alpha(colours.white, 0.1)}`,
            "&:hover": {
                boxShadow: `0 2px 3px ${alpha(colours.white, 0.1)}`
            }
        },
    };

    const getColors = (mode) => {
        switch (mode) {
            case THEME_MODES.HIGH_CONTRAST:
                return {
                    ...colours,
                    ...darkModePalettes,
                    black: colours.yellow100,
                };
            case THEME_MODES.COLOR_VISION:
                return {
                    ...colours,
                    primary100: "#F5C400",
                    critical100: "#b54f00",
                    success100: "#12785a",
                    green100: "#12785a",
                    dividergray: "#CFCFCF",
                    grayDark: colours.gray90,
                };
            case THEME_MODES.LIGHT:
                return { ...colours };
            case THEME_MODES.DARK:
            default:
                return {
                    ...colours,
                    ...darkModePalettes,
                    black: colours.white,
                };
        }
    };

    const currentColors = useMemo(() => getColors(themeMode), [themeMode]);
    const constLightMode = themeMode === THEME_MODES.LIGHT || themeMode === THEME_MODES.COLOR_VISION;

    // Create MUI Theme
    const fontFamily = ['Poppins', 'sans-serif'].join(',');
    const theme = useMemo(() =>
        createTheme({
            palette: {
                mode: constLightMode ? THEME_MODES.LIGHT : THEME_MODES.DARK,
                primary: {
                    main: currentColors.primary100,
                    contrastText: themeMode === THEME_MODES.COLOR_VISION ? currentColors.black : currentColors.white,
                },
                secondary: {
                    main: currentColors.purple100,
                },
                error: {
                    main: currentColors.error,
                },
                success: {
                    main: currentColors.success100,
                },
                warning: {
                    main: currentColors.critical100,
                },
                info: {
                    main: currentColors.grayDark,
                },
                background: {
                    paper: constLightMode ? currentColors.basegray : currentColors.black70,
                },
                text: {
                    primary: currentColors.black,
                    secondary: currentColors.grayDark,
                },
                common: {
                    black: currentColors.black,
                    white: currentColors.white,
                },
            },
            typography: {
                fontFamily,
                fontSize: FONT.SIZE_14,
                h1: { fontSize: FONT.SIZE_40 },
                h2: { fontSize: FONT.SIZE_32 },
                h3: { fontSize: FONT.SIZE_28 },
                h4: { fontSize: FONT.SIZE_24 },
                h5: { fontSize: FONT.SIZE_20 },
                h6: { fontSize: FONT.SIZE_16 },
            },
            components: {
                MuiButton: {
                    styleOverrides: {
                        sizeMedium: {
                            padding: "0.4375rem 1.25rem",
                            fontSize: FONT.SIZE_13,
                            fontWeight: 600,
                        },
                        sizeLarge: {
                            fontSize: FONT.SIZE_14,
                            padding: "0.675rem 1.25rem",
                            fontWeight: 500,
                        },
                        root: {
                            textTransform: "none",
                            borderRadius: 999,
                            boxShadow: 'none',
                            minWidth: "90px",
                        },
                    },
                },
                MuiButtonGroup: {
                    styleOverrides: {
                        firstButton: {
                            border: 0,
                        },
                        middleButton: {
                            border: 0,
                        },
                        contained: {
                            boxShadow: 'none',
                        }
                    }
                },
                MuiPaper: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            backgroundImage: "none",
                            boxShadow: theme.palette.mode === THEME_MODES.LIGHT ? `4px 4px 17px 0px ${theme.palette.common.black + "2A"}` : `6px 6px 10px #171717, -6px -6px 10px #2a2a2a`,
                        })
                    }
                },
                MuiMenu: {
                    styleOverrides: {
                        paper: ({ theme }) => ({
                            backgroundColor: currentColors.white,
                            borderRadius: "1rem",
                            boxShadow: theme.palette.mode === THEME_MODES.LIGHT ? `4px 4px 17px 0px ${theme.palette.common.black + "3A"}` : `0px 4px 13.2px 0px ${theme.palette.common.white + "5A"}`,
                            border: `1px solid ${theme.palette.mode === THEME_MODES.LIGHT ? currentColors.dividergray : currentColors.black80}`
                        }),
                    }
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            whiteSpace: "normal",
                            fontSize: FONT.SIZE_14,
                        },
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: {
                            '&.Mui-selected': {
                                backgroundColor: currentColors.primary10,
                                '&:hover': {
                                    backgroundColor: currentColors.primary10,
                                },
                            },
                        },
                    },
                },
                MuiPopper: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            '.MuiPaper-root': {
                                border: `1px solid ${theme.palette.mode === THEME_MODES.LIGHT ? currentColors.dividergray : currentColors.black80}`
                            }
                        }),
                    }
                },
                MuiFormLabel: {
                    styleOverrides: {
                        root: {
                            color: currentColors.black,
                            "&.MuiInputLabel-sizeSmall": {
                                fontSize: FONT.SIZE_13,
                            },
                            '&.Mui-disabled': {
                                color: themeMode === THEME_MODES.HIGH_CONTRAST ? alpha(currentColors.black, 0.5) : undefined,
                            },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            '&.Mui-disabled': {
                                color: themeMode === THEME_MODES.HIGH_CONTRAST ? currentColors.black : undefined,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeMode === THEME_MODES.HIGH_CONTRAST ? alpha(currentColors.black, 0.5) : undefined,
                                },
                            },
                        },
                        input: {
                            '&.Mui-disabled': {
                                WebkitTextFillColor: themeMode === THEME_MODES.HIGH_CONTRAST ? alpha(currentColors.black, 0.5) : undefined,
                            },
                        },
                        sizeSmall: {
                            fontSize: FONT.SIZE_13,
                        },
                        notchedOutline: ({ theme }) => ({
                            borderColor: theme.palette.mode === THEME_MODES.LIGHT ? currentColors.grayDark500 : currentColors.gray30,
                        }),
                    }
                },
                MuiSelect: {
                    styleOverrides: {
                        sizeSmall: {
                            fontSize: FONT.SIZE_13,
                        }
                    }
                },
                MuiDialogActions: {
                    styleOverrides: {
                        root: {
                            padding: "1rem 1.5rem",
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: ({ theme }) => ({
                            border: `1px solid ${theme.palette.mode === THEME_MODES.LIGHT ? currentColors.dividergray : currentColors.black80}`,
                            boxShadow: theme.palette.mode === THEME_MODES.LIGHT ? `4px 4px 17px 0px ${theme.palette.common.black + "3A"}` : `0px 4px 13.2px 0px ${theme.palette.common.white + "5A"}`,
                            '&.react-draggable': {
                                resize: 'both',
                                '&.MuiDialog-paper[style*="width"]': {
                                    maxWidth: 'inherit',
                                    minWidth: 220,
                                    minHeight: 220,
                                },
                                '.MuiDialogTitle-root': {
                                    cursor: 'move'
                                }
                            }
                        }),
                        backdrop: ({ theme }) => ({
                            backgroundColor: theme.palette.mode === THEME_MODES.DARK && alpha(colours.black, .7),
                        }),
                    },
                },
                MuiDialogTitle: {
                    styleOverrides: {
                        root: {
                            alignItems: 'center',
                        }
                    }
                },
                MuiTableCell: {
                    styleOverrides: {
                        sizeSmall: ({ theme }) => ({
                            height: 36,
                            borderBottomColor: theme.palette.common.black + "1A",
                            fontSize: FONT.SIZE_13,
                        }),
                        head: ({ theme }) => ({
                            fontWeight: 600,
                            backgroundColor: theme.palette.mode === THEME_MODES.LIGHT ? currentColors.grayDark500 : currentColors.gray50,
                            paddingTop: 8,
                            paddingBottom: 8,
                            height: 48,
                            borderBottomColor: currentColors.grayDisabled,
                            whiteSpace: 'nowrap',
                            "&.MuiTableCell-sizeSmall": {
                                backgroundColor: "transparent",
                                '&:first-of-type': {
                                    paddingLeft: 0,
                                },
                                '&:last-of-type': {
                                    paddingRight: 0,
                                },
                            }
                        }),
                        body: {
                            paddingTop: 8,
                            paddingBottom: 8,
                            height: 56,
                            borderBottomColor: currentColors.grayDisabled,
                            "&.MuiTableCell-sizeSmall": {
                                '&:first-of-type': {
                                    paddingLeft: 0,
                                },
                                '&:last-of-type': {
                                    paddingRight: 0,
                                },
                            }
                        },
                        footer: {
                            paddingTop: 8,
                            paddingBottom: 8,
                        },
                    },
                },
                MuiTable: {
                    styleOverrides: {
                        root: {
                            borderCollapse: "collapse",
                            '.MuiCheckbox-root': {
                                padding: 0,
                            }
                        },
                    },
                },
                MuiTablePagination: {
                    styleOverrides: {
                        root: {
                            overflow: 'visible',
                        },
                        toolbar: {
                            '& .MuiIconButton-root': {
                                '&:not(.Mui-disabled)': {
                                    color: currentColors.black,
                                },
                            },
                        },
                    },
                },
                MuiBreadcrumbs: {
                    styleOverrides: {
                        root: {
                            fontSize: FONT.SIZE_13,
                            color: currentColors.grayDark,
                        },
                    },
                },
                MuiLink: {
                    styleOverrides: {
                        root: {
                            color: currentColors.black,
                            "&.disabled": {
                                color: currentColors.grayDark,
                                pointerEvents: "none",
                            },
                        },
                    },
                },
                MuiSnackbar: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            [theme.breakpoints.up('sm')]: {
                                top: 16,
                            },
                        }),
                    },
                },
                MuiAlert: {
                    styleOverrides: {
                        filledSuccess: ({ theme }) => ({
                            backgroundColor: theme.palette.success.main,
                        }),
                        filledError: ({ theme }) => ({
                            backgroundColor: theme.palette.error.main,
                        }),
                        root: {
                            borderRadius: 8,
                            fontWeight: 400,
                            "pre": {
                                fontFamily,
                                margin: 0,
                            }
                        },
                    }
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: ({ theme }) => ({
                            backgroundColor: theme.palette.common.black,
                            fontSize: FONT.SIZE_14,
                            padding: ".5rem 1rem",
                            fontWeight: 400,
                        }),
                        arrow: ({ theme }) => ({
                            color: theme.palette.common.black,
                        }),
                    }
                },
                MuiChip: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            '&.MuiChip-colorDefault': {
                                backgroundColor: currentColors.dividergray,
                                color: theme.palette.mode === THEME_MODES.LIGHT ? currentColors.black : currentColors.white,
                            },
                        }),
                        avatar: {
                            marginLeft: 8,
                            marginRight: -8,
                            width: "1.75em",
                            height: "1.75em",
                            color: 'inherit',
                        },
                        colorWarning: {
                            color: themeMode === THEME_MODES.LIGHT && colours.white,
                        },
                        colorInfo: () => ({
                            backgroundColor:
                                themeMode === THEME_MODES.DARK || themeMode === THEME_MODES.HIGH_CONTRAST
                                    ? currentColors.gray50
                                    : themeMode === THEME_MODES.COLOR_VISION
                                        ? currentColors.dividergray
                                        : undefined,

                            color:
                                themeMode === THEME_MODES.DARK || themeMode === THEME_MODES.HIGH_CONTRAST
                                    ? colours.white
                                    : themeMode === THEME_MODES.COLOR_VISION
                                        ? colours.black
                                        : undefined,
                        }),
                        sizeSmall: {
                            fontSize: FONT.SIZE_12,
                            height: 22,
                        },
                        sizeMedium: {
                            height: 24,
                            lineHeight: 1.25,
                        }
                    }
                },
                MuiLinearProgress: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            height: 10,
                            borderRadius: 8,
                            boxShadow: `0px 1px 2px 0px ${theme.palette.mode === THEME_MODES.LIGHT ? theme.palette.common.black + "40" : theme.palette.common.white + "40"} inset`,
                            backgroundColor: theme.palette.mode === THEME_MODES.LIGHT ? currentColors.white : alpha(colours.white, 0.1),
                            "&.MuiLinearProgress-colorInherit": {
                                color: currentColors.white,
                                ".MuiLinearProgress-bar": {
                                    backgroundColor: colours.grayDisabled,
                                }
                            },
                            "&.MuiLinearProgress-colorSecondary, &.MuiLinearProgress-colorPrimary": {
                                ".MuiLinearProgress-bar": {
                                    backgroundImage: themeMode === THEME_MODES.COLOR_VISION ? CustomSvgIcons.linePattern : 'none',
                                    backgroundSize: '10px 10px',
                                    backgroundRepeat: 'repeat-x',
                                    backgroundPosition: 'center',
                                }
                            },
                            "&.MuiLinearProgress-colorWarning": {
                                ".MuiLinearProgress-bar": {
                                    backgroundImage: themeMode === THEME_MODES.COLOR_VISION ? CustomSvgIcons.WavePattern : 'none',
                                    backgroundSize: '75px 10px',
                                    backgroundRepeat: 'repeat-x',
                                    backgroundPosition: 'center',
                                }
                            },
                            "&.MuiLinearProgress-colorSuccess": {
                                ".MuiLinearProgress-bar": {
                                    backgroundImage: themeMode === THEME_MODES.COLOR_VISION ? CustomSvgIcons.circlePattern : 'none',
                                    backgroundSize: '9px 8px',
                                    backgroundRepeat: 'repeat-x',
                                    backgroundPosition: 'center',
                                }
                            },
                            ".MuiLinearProgress-bar": {
                                borderRadius: 8,
                            },
                        })
                    }
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            '&.MuiPaper-root': {
                                boxShadow: 'none',
                                borderRadius: 8,
                                border: `1px solid ${alpha(currentColors.black, 0.10)}`,
                                marginBottom: 8,
                                '&::before': {
                                    display: 'none'
                                }
                            },
                        }
                    }
                },
                MuiAccordionSummary: {
                    styleOverrides: {
                        content: {
                            marginTop: 16,
                            marginBottom: 16,
                            alignItems: 'center',
                            '&.Mui-expanded': {
                                marginTop: 16,
                                marginBottom: 16,
                            },
                        },
                        expanded: {
                            minHeight: 'inherit',
                        },
                        expandIconWrapper: {
                            color: alpha(currentColors.black, 0.5),
                        }
                    }
                },
                MuiSwitch: {
                    styleOverrides: {
                        track: {
                            backgroundColor: currentColors.grayDark,
                        }
                    }
                },
            },
        }),
        [themeMode, currentColors, fontSizeLevel]
    );

    return (
        <ThemeContext.Provider
            value={{
                colours: currentColors,
                toggleTheme,
                themeMode,
                setThemeMode,
                FONT,
            }}
        >
            <ThemeProvider theme={theme}><CssBaseline />{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};